import { useState, useEffect } from 'react';
import { schoolYearApi } from '../lib/api';
import {
  Plus,
  Calendar,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function SchoolYearsPage() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newYear, setNewYear] = useState({
    name: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchYears();
  }, []);

  const fetchYears = async () => {
    setLoading(true);
    try {
      const response = await schoolYearApi.getAll();
      setYears(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des années scolaires');
    } finally {
      setLoading(false);
    }
  };

  const handleAddYear = async (e) => {
    e.preventDefault();
    if (!newYear.name || !newYear.start_date || !newYear.end_date) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setFormLoading(true);
    try {
      await schoolYearApi.create(newYear);
      toast.success('Année scolaire créée');
      setShowAddDialog(false);
      setNewYear({ name: '', start_date: '', end_date: '' });
      fetchYears();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleActivate = async (yearId) => {
    try {
      await schoolYearApi.activate(yearId);
      toast.success('Année scolaire activée');
      fetchYears();
    } catch (error) {
      toast.error('Erreur lors de l\'activation');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="school-years-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Années Scolaires</h1>
          <p className="text-muted-foreground text-sm">Gérez les années scolaires de l'établissement</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-year-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle année
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Nouvelle année scolaire</DialogTitle>
              <DialogDescription>Créer une nouvelle année scolaire</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddYear} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom de l'année *</Label>
                <Input
                  value={newYear.name}
                  onChange={(e) => setNewYear({ ...newYear, name: e.target.value })}
                  placeholder="Ex: 2024-2025"
                  data-testid="year-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input
                    type="date"
                    value={newYear.start_date}
                    onChange={(e) => setNewYear({ ...newYear, start_date: e.target.value })}
                    data-testid="year-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin *</Label>
                  <Input
                    type="date"
                    value={newYear.end_date}
                    onChange={(e) => setNewYear({ ...newYear, end_date: e.target.value })}
                    data-testid="year-end"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="save-year-btn">
                  {formLoading ? 'Création...' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Years List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <Skeleton className="h-4 w-48" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : years.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground mb-4">Aucune année scolaire créée</p>
            <Button variant="outline" className="rounded-full" onClick={() => setShowAddDialog(true)}>
              Créer une année scolaire
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {years.map((year) => (
            <Card
              key={year.id}
              className={cn(
                'card-hover',
                year.is_active && 'border-primary border-2'
              )}
              data-testid={`year-card-${year.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        year.is_active ? 'bg-primary/10' : 'bg-muted'
                      )}
                    >
                      <Calendar
                        className={cn(
                          'w-5 h-5',
                          year.is_active ? 'text-primary' : 'text-muted-foreground'
                        )}
                      />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground">{year.name}</h3>
                      {year.is_active && (
                        <Badge className="mt-1 bg-primary text-primary-foreground">
                          Active
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>Début: {formatDate(year.start_date)}</p>
                  <p>Fin: {formatDate(year.end_date)}</p>
                </div>

                {!year.is_active && (
                  <Button
                    variant="outline"
                    className="w-full mt-4 rounded-full"
                    onClick={() => handleActivate(year.id)}
                    data-testid={`activate-year-${year.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Activer
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
